import { useEffect, useState } from "react";
import { Megaphone, Plus, Pencil, Trash2, Save, X, Loader2, ToggleLeft, ToggleRight } from "lucide-react";
import AdminHeader from "@/components/AdminHeader";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminOffersTicker() {
  const [data, setData] = useState({ enabled: true, items: [] });
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/offers-ticker");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async () => {
    setToggling(true);
    try {
      const { data: fresh } = await api.post("/admin/offers-ticker/toggle", {
        enabled: !data.enabled,
      });
      setData(fresh);
      toast.success(fresh.enabled ? "Ticker turned ON" : "Ticker turned OFF");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setToggling(false);
    }
  };

  const add = async () => {
    const t = newText.trim();
    if (!t) return;
    setAdding(true);
    try {
      const { data: fresh } = await api.post("/admin/offers-ticker/items", { text: t });
      setData(fresh);
      setNewText("");
      toast.success("Offer added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditingText(item.text);
  };

  const saveEdit = async () => {
    if (!editingText.trim()) return;
    setSaving(true);
    try {
      const { data: fresh } = await api.patch(
        `/admin/offers-ticker/items/${editingId}`,
        { text: editingText.trim() }
      );
      setData(fresh);
      setEditingId(null);
      setEditingText("");
      toast.success("Offer updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    try {
      const { data: fresh } = await api.delete(`/admin/offers-ticker/items/${id}`);
      setData(fresh);
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <AdminHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1
            className="text-2xl sm:text-3xl font-black text-emerald-400 flex items-center gap-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            <Megaphone size={22} /> Offers Ticker
          </h1>
          <button
            onClick={toggle}
            disabled={toggling || loading}
            data-testid="ticker-toggle"
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold border transition-colors ${
              data.enabled
                ? "bg-emerald-500 text-black border-emerald-400 hover:bg-emerald-400"
                : "bg-black/30 text-white/80 border-white/20 hover:bg-white/10"
            }`}
          >
            {toggling ? (
              <Loader2 size={16} className="animate-spin" />
            ) : data.enabled ? (
              <ToggleRight size={16} />
            ) : (
              <ToggleLeft size={16} />
            )}
            {data.enabled ? "Ticker is ON" : "Ticker is OFF"}
          </button>
        </div>

        <section className="bg-zinc-900 rounded-2xl border border-white/10 p-5 space-y-3">
          <label className="text-xs font-semibold text-white/70">
            Add a new offer message
          </label>
          <div className="flex gap-2">
            <input
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="e.g. Refer 5 friends and get instant ₹500 bonus"
              maxLength={280}
              data-testid="ticker-add-input"
              className="flex-1 bg-black/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-400/60"
            />
            <button
              onClick={add}
              disabled={adding || !newText.trim()}
              data-testid="ticker-add-btn"
              className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold rounded-lg px-4 disabled:opacity-60"
            >
              {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Add
            </button>
          </div>
          <p className="text-[11px] text-white/40">
            Up to 280 characters per offer. Emojis welcome.
          </p>
        </section>

        <section
          data-testid="ticker-list"
          className="bg-zinc-900 rounded-2xl border border-white/10 overflow-hidden"
        >
          <div className="px-5 py-3 border-b border-white/10 font-bold text-white/90">
            All Offers <span className="text-white/40 text-sm">({data.items.length})</span>
          </div>
          {loading ? (
            <div className="py-12 text-center text-white/50">Loading…</div>
          ) : data.items.length === 0 ? (
            <div
              data-testid="ticker-empty"
              className="py-12 text-center text-white/60"
            >
              No offers yet. Add the first one above.
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {data.items.map((item, idx) => (
                <li
                  key={item.id}
                  data-testid={`ticker-item-${item.id}`}
                  className="px-5 py-3 flex items-start gap-3"
                >
                  <div className="mt-1 h-6 w-6 shrink-0 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center text-xs font-bold">
                    {idx + 1}
                  </div>
                  {editingId === item.id ? (
                    <input
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                      autoFocus
                      data-testid={`ticker-edit-input-${item.id}`}
                      className="flex-1 bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-400/60"
                    />
                  ) : (
                    <div className="flex-1 text-sm text-white/90 min-w-0 break-words">
                      {item.text}
                    </div>
                  )}
                  {editingId === item.id ? (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={saveEdit}
                        disabled={saving}
                        data-testid={`ticker-save-${item.id}`}
                        className="text-emerald-400 hover:text-emerald-300 p-1.5"
                        title="Save"
                      >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                      </button>
                      <button
                        onClick={() => { setEditingId(null); setEditingText(""); }}
                        className="text-white/60 hover:text-white p-1.5"
                        title="Cancel"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => startEdit(item)}
                        data-testid={`ticker-edit-${item.id}`}
                        className="text-amber-400 hover:text-amber-300 p-1.5"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => del(item.id)}
                        data-testid={`ticker-delete-${item.id}`}
                        className="text-red-400 hover:text-red-300 p-1.5"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
